import React, { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import {
	TitleComponent,
	TooltipComponent,
	GridComponent,
	LegendComponent,
	DataZoomComponent,
} from 'echarts/components';

// Register base components once
echarts.use([
	TitleComponent,
	TooltipComponent,
	GridComponent,
	LegendComponent,
	DataZoomComponent,
	CanvasRenderer,
]);

// Default theme aligned with Velzon template (fallbacks via CSS variables)
export const buildChartTheme = ({
	fontFamily = 'var(--vz-body-font-family, Inter, Roboto, Helvetica, Arial, sans-serif)',
	fontSize = 11,
	splitLineColor = '#e9ebec',
} = {}) => ({
	textStyle: {
		fontFamily,
		fontSize,
		// keep default echarts color
	},
	grid: {
		top: '12%',
		left: '3%',
		right: '4%',
		bottom: '15%',
		containLabel: true,
	},
	legend: {
		textStyle: { fontSize },
	},
	tooltip: {
		textStyle: { fontSize },
	},
	// Note: do NOT include xAxis/yAxis here so pie/donut charts don't get axes
});

export const withChartTheme = (options, themeOverrides) => {
	const theme = buildChartTheme(themeOverrides);
	// Merge shallowly while preserving provided arrays/objects from options
	const merged = {
		...theme,
		...options,
		legend: { ...theme.legend, ...(options?.legend || {}) },
		tooltip: { ...theme.tooltip, ...(options?.tooltip || {}) },
		grid: { ...theme.grid, ...(options?.grid || {}) },
	};
	// Only apply axes if caller provided axes; avoid axes for pies
	if (options?.xAxis) {
		const axisBase = { axisLabel: { fontSize: theme.textStyle.fontSize } };
		const mergeAxis = x => ({...axisBase, ...x, axisLabel: { ...axisBase.axisLabel, ...(x?.axisLabel || {}) },});
		merged.xAxis = Array.isArray(options.xAxis)
			? options.xAxis.map(mergeAxis)
			: mergeAxis(options.xAxis);
	}
	if (options?.yAxis) {
		const axisBase = {
			axisLabel: { fontSize: theme.textStyle.fontSize },
			splitLine: { show: true, lineStyle: { color: '#e9ebec' } },
		};
		const mergeAxis = y => ({...axisBase, ...y, axisLabel: { ...axisBase.axisLabel, ...(y?.axisLabel || {}) }, });
		merged.yAxis = Array.isArray(options.yAxis)
			? options.yAxis.map(mergeAxis)
			: mergeAxis(options.yAxis);
	}
	return merged;
};

/**
 * OChartBase - low-level ECharts renderer with robust resize handling
 */
export const OChartBase = ({ options, style, minHeight, maxHeight }) => {
	const chartRef = useRef(null);

	const safeOptions = useMemo(() => options || {}, [options]);

	// Initialize once and handle resize
	useEffect(() => {
		if (!chartRef.current) return undefined;
		let chartInstance = echarts.getInstanceByDom(chartRef.current);
		if (!chartInstance) {
			chartInstance = echarts.init(chartRef.current);
		}
		const handleWindowResize = () => chartInstance?.resize();
		window.addEventListener('resize', handleWindowResize);
		const resizeObserver = new ResizeObserver(() => {
			if (!chartRef.current) return;
			const inst = echarts.getInstanceByDom(chartRef.current);
			inst?.resize();
		});
		resizeObserver.observe(chartRef.current);
		return () => {
			window.removeEventListener('resize', handleWindowResize);
			resizeObserver.disconnect();
			chartInstance?.dispose();
		};
	}, []);

	// Apply options - use notMerge to replace data cleanly while preserving animations
	useEffect(() => {
		if (!chartRef.current) return;
		const chartInstance =
			echarts.getInstanceByDom(chartRef.current) ||
			echarts.init(chartRef.current);
		try {
			chartInstance.setOption(
				{
					animation: true,
					animationDuration: 600,
					animationEasing: 'cubicOut',
					...safeOptions,
				},
				{
					notMerge: false,
					replaceMerge: ['series', 'xAxis', 'yAxis', 'legend'],
				}
			);
			setTimeout(() => chartInstance?.resize(), 0);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('[OChartBase] setOption error', err);
		}
	}, [safeOptions]);

	return (
		<div
			ref={chartRef}
			style={{
				width: '100%',
				height: maxHeight || minHeight || '300px',
				minHeight: minHeight || '200px',
				...style,
			}}
		/>
	);
};

export default OChartBase;
